import { format } from "date-fns/format";

type ThemeRule = {
  label: string;
  keywords: string[];
};

export const BLOG_THEME_RULES: ThemeRule[] = [
  {
    label: "VTCT Pathways and Qualifications",
    keywords: [
      "vtct",
      "qualification",
      "pathway",
      "level 3",
      "level 4",
      "level 5",
      "level 7",
      "regulation",
      "licensing",
      "non-medic",
      "medic",
    ],
  },
  {
    label: "Injectables and Advanced Practice",
    keywords: [
      "injectable",
      "injectables",
      "anti wrinkle injections",
      "filler",
      "aesthetic practitioner",
      "advanced skin",
      "complication",
    ],
  },
  {
    label: "Laser and Device Treatments",
    keywords: [
      "laser",
      "ipl",
      "tattoo removal",
      "tattoo",
      "hair removal",
      "device-led",
    ],
  },
  {
    label: "Career, Finance and Clinic Growth",
    keywords: [
      "career",
      "salary",
      "pricing",
      "finance",
      "insurance",
      "profit",
      "revenue",
      "income",
      "growth",
      "business",
    ],
  },
];

/** Explicit article themes that are not keyword-inferred (frontmatter labels). */
export const EXPLICIT_BLOG_THEME_LABELS = [
  "Choosing an Aesthetics Training Route",
  "Choosing an Aesthetics Training Provider",
] as const;

/** Stable theme labels used for category URLs and soft-empty ISR fallbacks. */
export const CANONICAL_BLOG_THEME_LABELS = [
  ...BLOG_THEME_RULES.map((rule) => rule.label),
  ...EXPLICIT_BLOG_THEME_LABELS,
  "Guides and Insights",
] as const;

export const slugifyTheme = (value = "") =>
  value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
export const stripMarkdownSyntax = (value = "") => {
  if (!value) {
    return "";
  }

  return value
    .replace(/!\[.*?\]\(.*?\)/g, " ")
    .replace(/\[([^\]]+)\]\(([^)]+)\)/g, "$1")
    .replace(/`{1,3}([^`]+)`{1,3}/g, "$1")
    .replace(/\*\*([^*]+)\*\*/g, "$1")
    .replace(/__([^_]+)__/g, "$1")
    .replace(/\*([^*\n]+)\*/g, "$1")
    .replace(/_([^_\n]+)_/g, "$1")
    .replace(/~~([^~]+)~~/g, "$1")
    .replace(/^>\s?/gm, "")
    .replace(/^#{1,6}\s+/gm, "")
    .replace(/^\s*([-*+]|\d+\.)\s+/gm, "")
    .replace(/\|/g, " ")
    .replace(/\s+/g, " ")
    .trim();
};

export const buildArticleListingExcerpt = (
  markdownContent = "",
  maxLength = 220
) => {
  const plainText = stripMarkdownSyntax(markdownContent);
  if (!plainText) {
    return "";
  }

  if (plainText.length <= maxLength) {
    return plainText;
  }

  return `${plainText.slice(0, maxLength).trim()}...`;
};

/** Keep the first article for each URL (local markdown listings should precede CMS). */
export const dedupeArticlesByUrl = <T extends { fields?: { url?: unknown } }>(
  articles: T[]
): T[] => {
  const seen = new Set<string>();

  return articles.filter((article) => {
    const rawUrl = article?.fields?.url;
    if (typeof rawUrl !== "string" || !rawUrl.trim()) {
      return true;
    }

    const normalized = rawUrl.endsWith("/") ? rawUrl : `${rawUrl}/`;
    if (seen.has(normalized)) {
      return false;
    }

    seen.add(normalized);
    return true;
  });
};

export const normalizeThemeLabel = (value = "") => {
  if (!value || typeof value !== "string") {
    return "";
  }
  return value
    .replace(/[-_]/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (match) => match.toUpperCase());
};

export function extractPlainTextFromRichText(richText: any) {
  if (!richText) return "";

  if (richText.content?.[0]?.content?.[0]?.value) {
    return richText.content[0].content[0].value;
  }

  if (richText.content && Array.isArray(richText.content)) {
    const extractText = (node: any): string => {
      if (node.nodeType === "text" && node.value) {
        return node.value;
      }
      if (node.content && Array.isArray(node.content)) {
        return node.content.map(extractText).filter(Boolean).join(" ");
      }
      return "";
    };

    return richText.content.map(extractText).filter(Boolean).join(" ").trim();
  }

  return "";
}

export const inferArticleTheme = (article: any) => {
  const explicitTheme =
    normalizeThemeLabel(article?.fields?.theme) ||
    normalizeThemeLabel(
      Array.isArray(article?.fields?.themes) ? article.fields.themes[0] : ""
    ) ||
    normalizeThemeLabel(
      Array.isArray(article?.fields?.categories)
        ? article.fields.categories[0]
        : ""
    );

  if (explicitTheme) {
    return explicitTheme;
  }

  const sourceText = [
    article?.fields?.title,
    article?.fields?.description,
    article?.fields?.targetKeyword,
    article?.fields?.keywords,
    Array.isArray(article?.fields?.secondaryKeywords)
      ? article.fields.secondaryKeywords.join(" ")
      : "",
    article?.fields?.url,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();

  const matchedTheme = BLOG_THEME_RULES.find((rule) =>
    rule.keywords.some((keyword) => sourceText.includes(keyword))
  );

  return matchedTheme?.label || "Guides and Insights";
};

export const getThemeLabelFromSlug = (slug = "") => {
  const normalizedSlug = slugifyTheme(slug);
  const fromCanonical = CANONICAL_BLOG_THEME_LABELS.find(
    (label) => slugifyTheme(label) === normalizedSlug
  );

  if (fromCanonical) {
    return fromCanonical;
  }

  return normalizeThemeLabel(slug) || "Guides and Insights";
};

export const isKnownBlogThemeSlug = (slug = "") => {
  const normalizedSlug = slugifyTheme(slug);
  return CANONICAL_BLOG_THEME_LABELS.some(
    (label) => slugifyTheme(label) === normalizedSlug
  );
};

export const getArticlePublishedAt = (article: any) => {
  const dateValue =
    article?.fields?.publishedDate ||
    article?.fields?.date ||
    article?.sys?.createdAt ||
    null;

  const parsed = dateValue ? new Date(dateValue).getTime() : NaN;
  if (Number.isNaN(parsed)) {
    return 0;
  }

  return parsed;
};

export const getArticlePublishedLabel = (article: any) => {
  const timestamp = getArticlePublishedAt(article);
  if (!timestamp) {
    return "Recently updated";
  }

  return format(new Date(timestamp), "do MMMM yyyy");
};

export const getArticleExcerpt = (article: any) => {
  const richTextExcerpt = extractPlainTextFromRichText(article?.fields?.listingText);
  const description = article?.fields?.description || "";
  const combined = stripMarkdownSyntax(
    [richTextExcerpt, description].filter(Boolean).join(" ")
  );

  return combined.length > 185 ? `${combined.slice(0, 182).trim()}...` : combined;
};

export const getArticleReadTime = (article: any) => {
  const sourceText = [
    extractPlainTextFromRichText(article?.fields?.listingText),
    article?.fields?.description || "",
  ]
    .filter(Boolean)
    .join(" ");

  const words = sourceText.split(/\s+/).filter(Boolean).length;
  return Math.max(1, Math.round(words / 220));
};
